import React from "react"
import { Form, Field, Formik } from "formik"
import { useNavigate } from "react-router-dom"
import Modal from "../../Modal/Modal"
import Input from "../../FormComponents/Input"
import axiosService from "../../../utils/AxiosService"
import { toastError } from "../../FormComponents/Toasts"
import { providerLogo, formatProvider } from "../../../utils/Helper"
import { NewManualInfluencerSchema } from "../../../schemas/NewManualInfluencerSchema"
import { UGC_CREATOR_TAG_NAME } from "../../../utils/tagStyles"

const PROVIDERS = ['instagram', 'tiktok', 'youtube', 'linkedin', 'pinterest', 'podcast']

export default function NewInfluencerModal({ isOpen, setIsOpen }) {
  const navigate = useNavigate()

  const handleSubmit = (values, { setSubmitting }) => {
    axiosService.post('/api/project_manager/authentications', {
      authentication: {
        manual: true,
        full_name: values.full_name,
        provider: values.provider,
        username: values.username,
      }
    })
      .then((authentication) => {
        const tagRequest = values.tag_as_ugc
          ? axiosService.post(`/api/project_manager/authentications/${authentication.id}/tags/`, { tag: { name: UGC_CREATOR_TAG_NAME } })
          : Promise.resolve()

        // The influencer itself is already created at this point - don't block
        // navigating to the new profile on the (optional) tag call.
        tagRequest.catch(({ data: { error } } = { data: {} }) => toastError(error || 'Failed to add UGC Creator tag'))

        setIsOpen(false)
        navigate(`/project_manager/authentications/${authentication.id}/influencer/overview`)
      })
      .catch(({ data: { error } }) => {
        toastError(error)
        setSubmitting(false)
      })
  }

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <Modal.Header>
        <span className="font-bold dark:text-white">Create influencer manually</span>
      </Modal.Header>
      <Modal.Body>
        <div className="min-w-[420px]">
          <p className="text-sm text-gray-500 dark:text-gray-400 -mt-2 mb-4">
            For creators too small to be in Modash yet. Reach, bio, and further accounts can be added once they're created.
          </p>
          <Formik
            initialValues={{ full_name: '', provider: '', username: '', tag_as_ugc: true }}
            validationSchema={NewManualInfluencerSchema}
            onSubmit={handleSubmit}
          >
            {({ handleBlur, handleChange, values, touched, errors, setFieldValue, isSubmitting }) => (
              <Form className="flex flex-col">
                <div className="form-item-container w-full">
                  <Input error={touched.full_name && errors.full_name} label="Full Name" name="full_name" onChange={handleChange} onBlur={handleBlur} value={values.full_name} />
                </div>
                <div className="form-item-container w-full pt-2">
                  <p className="text-sm pb-1 dark:text-white">Provider</p>
                  <div role="group" className="flex items-center gap-2 flex-wrap">
                    {PROVIDERS.map((provider) => (
                      <label
                        key={provider}
                        title={formatProvider(provider)}
                        className={`${values.provider === provider ? 'bg-rb-soft-green dark:bg-rb-green/20 border-rb-green dark:border-rb-green' : 'bg-white dark:bg-rb-dark-mode'} cursor-pointer w-12 h-12 flex items-center justify-center border-1 dark:border-rb-dark-mode-lighter rounded-md`}
                      >
                        <Field type="radio" name="provider" value={provider} className="hidden" onChange={(e) => setFieldValue('provider', e.target.value)} />
                        {providerLogo(provider)}
                      </label>
                    ))}
                  </div>
                  {touched.provider && errors.provider && <div className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.provider}</div>}
                </div>
                <div className="form-item-container w-full">
                  <Input error={touched.username && errors.username} label="Username" name="username" onChange={handleChange} onBlur={handleBlur} value={values.username} />
                </div>
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <Field type="checkbox" name="tag_as_ugc" checked={values.tag_as_ugc} onChange={handleChange} />
                  <span className="text-sm dark:text-white">Tag as UGC Creator</span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Marks this profile so it can be filtered for later. PMs can remove the tag anytime, e.g. if this creator grows and is no longer managed manually.
                </p>
                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    className="rb-btn-cancel font-bold text-md p-2 px-4 rounded-lg disabled:opacity-50"
                    disabled={isSubmitting}
                    onClick={() => setIsOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="ml-2 bg-rb-green text-white rounded-lg p-2 px-4 disabled:opacity-50"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Modal.Body>
    </Modal>
  )
}
